import { useMemo, useState } from 'react';
import {
  CONTEST_FORM_SECTIONS,
  contestCandidateName,
  contestFieldVisible,
  contestProjectTitle,
  formatContestFieldValue,
  type ContestFormData,
} from '@isbd/shared';
import { downloadTextFile } from '@/lib/downloadText';

interface Props {
  formData: ContestFormData | Record<string, string> | null | undefined;
  meta?: {
    contestName?: string;
    createdAt?: string;
    userId?: string;
    registrationId?: string;
  };
}

export function ContestDossierPanel({ formData, meta }: Props) {
  const data = (formData ?? {}) as ContestFormData;
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(CONTEST_FORM_SECTIONS.map((s) => [s.key, true])),
  );

  const title = contestProjectTitle(data) || 'Dossier sans titre';
  const candidate = contestCandidateName(data) || '—';

  const filledCount = useMemo(() => {
    let filled = 0;
    let total = 0;
    for (const section of CONTEST_FORM_SECTIONS) {
      for (const field of section.fields) {
        if (field.type === 'info' || !contestFieldVisible(field, data)) continue;
        total += 1;
        const raw = data[field.key] ?? '';
        if (field.type === 'checkbox') {
          if (raw === 'true') filled += 1;
        } else if (raw.trim()) {
          filled += 1;
        }
      }
    }
    return { filled, total };
  }, [data]);

  function toggleSection(key: string) {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function setAll(open: boolean) {
    setOpenSections(Object.fromEntries(CONTEST_FORM_SECTIONS.map((s) => [s.key, open])));
  }

  function downloadForm() {
    const lines: string[] = [
      `Projet : ${title}`,
      `Candidat : ${candidate}`,
      meta?.contestName ? `Concours : ${meta.contestName}` : '',
      meta?.createdAt ? `Date de dépôt : ${new Date(meta.createdAt).toLocaleString('fr-FR')}` : '',
      '',
    ].filter(Boolean);
    for (const section of CONTEST_FORM_SECTIONS) {
      if (section.key === 'documents') continue;
      lines.push(`${section.number}. ${section.label}`);
      for (const field of section.fields) {
        if (field.type === 'info' || !contestFieldVisible(field, data)) continue;
        lines.push(`- ${field.label} : ${formatContestFieldValue(field, data)}`);
      }
      lines.push('');
    }
    downloadTextFile(`dossier-concours-${meta?.registrationId ?? 'candidat'}.txt`, lines.join('\n'));
  }

  return (
    <div>
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '1rem',
          flexWrap: 'wrap',
          marginBottom: '1rem',
          paddingBottom: '0.85rem',
          borderBottom: '1px solid var(--line)',
        }}
      >
        <div>
          <h3 style={{ margin: '0 0 0.35rem' }}>{title}</h3>
          <p className="muted" style={{ margin: 0 }}>
            Candidat : <strong style={{ color: 'var(--ink)' }}>{candidate}</strong>
            {meta?.contestName ? ` · ${meta.contestName}` : ''}
            {meta?.createdAt
              ? ` · déposé le ${new Date(meta.createdAt).toLocaleDateString('fr-FR')}`
              : ''}
          </p>
          {meta?.registrationId ? (
            <p className="muted" style={{ margin: '0.25rem 0 0', fontSize: '0.85rem' }}>
              Réf. {meta.registrationId}
            </p>
          ) : null}
        </div>
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <span className="chip">
            {filledCount.filled}/{filledCount.total} champs
          </span>
          <button type="button" className="btn btn-ghost" onClick={() => setAll(true)}>
            Tout ouvrir
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => setAll(false)}>
            Tout fermer
          </button>
          <button type="button" className="btn btn-ghost" onClick={downloadForm}>
            Télécharger le formulaire
          </button>
        </div>
      </header>

      <div style={{ display: 'grid', gap: '0.75rem' }}>
        {CONTEST_FORM_SECTIONS.map((section) => {
          const fields = section.fields.filter(
            (f) => f.type !== 'info' && contestFieldVisible(f, data),
          );
          if (section.key === 'documents') {
            // Rubrique informative — pas de réponses à afficher ici
            return null;
          }
          const isOpen = openSections[section.key] !== false;

          return (
            <section
              key={section.key}
              className="panel"
              style={{ margin: 0, padding: 0, overflow: 'hidden' }}
            >
              <button
                type="button"
                onClick={() => toggleSection(section.key)}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.85rem 1rem',
                  border: 0,
                  background: 'rgba(15, 40, 70, 0.03)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: 'inherit',
                }}
              >
                <strong>
                  {section.number}. {section.label}
                </strong>
                <span className="muted" style={{ fontSize: '0.85rem' }}>
                  {isOpen ? 'Masquer' : 'Afficher'} · {fields.length} champ
                  {fields.length > 1 ? 's' : ''}
                </span>
              </button>

              {isOpen ? (
                <dl
                  style={{
                    margin: 0,
                    padding: '0.75rem 1rem 1rem',
                    display: 'grid',
                    gap: '0.65rem',
                  }}
                >
                  {fields.length === 0 ? (
                    <p className="muted" style={{ margin: 0 }}>
                      Aucune donnée pour cette rubrique.
                    </p>
                  ) : (
                    fields.map((field) => {
                      const value = formatContestFieldValue(field, data);
                      const isLong =
                        field.type === 'textarea' || (value.length > 120 && value !== '—');
                      return (
                        <div
                          key={field.key}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: isLong ? '1fr' : 'minmax(10rem, 32%) 1fr',
                            gap: isLong ? '0.2rem' : '0.75rem',
                            paddingBottom: '0.55rem',
                            borderBottom: '1px solid rgba(15,40,70,0.06)',
                          }}
                        >
                          <dt
                            className="muted"
                            style={{ margin: 0, fontSize: '0.88rem', fontWeight: 600 }}
                          >
                            {field.label}
                          </dt>
                          <dd
                            style={{
                              margin: 0,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              lineHeight: 1.45,
                            }}
                          >
                            {value}
                          </dd>
                        </div>
                      );
                    })
                  )}
                </dl>
              ) : null}
            </section>
          );
        })}
      </div>
    </div>
  );
}
